// Lead Generation System — Lead Generator detail. Profile + roster actions +
// their recent preleads + the current week's earnings summary + the manager's
// coaching-feedback thread (leave a note; the generator reads it in their own
// console, where it also drives the sidebar "Feedback" unread badge).

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Pencil } from "lucide-react";
import { Badge, Button, Card, EmptyState, OverflowMenu, Skeleton, Textarea, useToast } from "@/design-system/components/ui";
import type { OverflowMenuItem } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { calendarDateInputToIso, displayEmail, formatPhone } from "@/design-system/lib/format";
import type { PreleadSummaryDto } from "@/api/leadGen/preleads";
import { usePreleads } from "@/hooks/leadGen/usePreleads";
import {
  useArchiveLeadGenerator,
  useInviteLeadGenerator,
  useLeadGenerator,
  useSetLeadGeneratorStatus,
} from "@/hooks/leadGen/useLeadGenerators";
import { useEarningsSummary } from "@/hooks/leadGen/useEarnings";
import { useRevertPlan } from "@/hooks/leadGen/useCompensationPlan";
import { useLeadGenFeedback, usePostLeadGenFeedback } from "@/hooks/leadGen/useLeadGenFeedback";
import {
  GENERATOR_STATUS_BADGE,
  GENERATOR_STATUS_LABEL,
  INTEREST_BADGE,
  PRELEAD_STATUS_BADGE,
  PRELEAD_STATUS_LABEL,
  money,
  preleadDisplayName,
  todayInputDate,
} from "./leadGenShared";
import { GeneratorFormModal } from "./GeneratorFormModal";

export function GeneratorDetailPage() {
  const { id = "" } = useParams();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("LeadGen.Manage");
  const canManagePlan = hasPermission("LeadGen.ManagePlan");
  const toast = useToast();
  const { date, tz } = useDateFormatter();
  const [editOpen, setEditOpen] = useState(false);

  const detail = useLeadGenerator(id);
  const preleads = usePreleads({ leadGeneratorId: id, page: 1, pageSize: 10 });
  const weekIso = useMemo(() => calendarDateInputToIso(todayInputDate()) ?? "", []);
  const summary = useEarningsSummary(id, weekIso);

  const invite = useInviteLeadGenerator();
  const setStatus = useSetLeadGeneratorStatus();
  const archive = useArchiveLeadGenerator();
  const revertPlan = useRevertPlan();
  const onError = (err: unknown) => toast.error(extractErrorMessage(err));

  const g = detail.data;

  const columns: Column<PreleadSummaryDto>[] = useMemo(
    () => [
      {
        key: "business",
        header: "Business / contact",
        render: (p) =>
          preleadDisplayName({
            businessName: p.businessName,
            contactFirstName: p.contactFirstName,
            contactLastName: p.contactLastName,
            rawText: p.rawTextSnippet,
            listingUrl: p.listingUrl,
            createdAt: p.createdAt,
          }, tz),
      },
      {
        key: "status",
        header: "Status",
        render: (p) => <Badge status={PRELEAD_STATUS_BADGE[p.status]}>{PRELEAD_STATUS_LABEL[p.status]}</Badge>,
      },
      {
        key: "interest",
        header: "Interest",
        mobileHidden: true,
        render: (p) => <Badge status={INTEREST_BADGE[p.interestLevel]}>{p.interestLevel}</Badge>,
      },
      {
        key: "escalated",
        header: "Escalated",
        render: (p) =>
          p.escalatedToLeadId ? (
            <Link
              to={`/leads/${p.escalatedToLeadId}`}
              className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:underline"
            >
              View lead <ArrowUpRight size={13} strokeWidth={1.5} aria-hidden />
            </Link>
          ) : (
            <span className="text-caption text-gray-mid">—</span>
          ),
      },
      {
        key: "created",
        header: "Created",
        mobileHidden: true,
        render: (p) => <span className="text-caption">{date(p.createdAt)}</span>,
      },
    ],
    [date, tz],
  );

  if (detail.isLoading) {
    return (
      <div className="flex flex-col gap-space-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (detail.isError || !g) {
    return (
      <EmptyState
        title="Generator not found"
        description={detail.isError ? extractErrorMessage(detail.error) : "This lead generator no longer exists."}
      />
    );
  }

  const actionItems: OverflowMenuItem[] = [
    { label: g.portalUserId ? "Re-send portal invite" : "Send portal invite", onSelect: () => invite.mutate(id, { onSuccess: (res) => toast.success(res.isExistingAccount ? "Invitation re-sent" : `Invitation sent to ${res.email}`), onError }) },
  ];
  if (g.status === "Active") {
    actionItems.push({ label: "Suspend", onSelect: () => setStatus.mutate({ id, status: "Suspended" }, { onSuccess: () => toast.success("Suspended"), onError }) });
  } else if (g.status === "Suspended" || g.status === "Inactive" || g.status === "Invited") {
    // Invited included as a manual safety net: activation normally fires
    // automatically when the generator redeems their portal invite.
    actionItems.push({ label: g.status === "Invited" ? "Activate" : "Reactivate", onSelect: () => setStatus.mutate({ id, status: "Active" }, { onSuccess: () => toast.success(g.status === "Invited" ? "Activated" : "Reactivated"), onError }) });
  }
  if (canManagePlan && g.hasCustomPlan) {
    actionItems.push({
      label: "Revert to program default",
      confirm: "Revert this generator to the program default plan? Their compensation-plan override will be removed.",
      onSelect: () =>
        revertPlan.mutate(id, {
          onSuccess: () => toast.success("Reverted to program default"),
          onError,
        }),
    });
  }
  actionItems.push({
    label: g.isArchived ? "Unarchive" : "Archive",
    destructive: !g.isArchived,
    confirm: g.isArchived ? undefined : "Archive this generator?",
    onSelect: () => archive.mutate({ id, archived: g.isArchived }, { onSuccess: () => toast.success(g.isArchived ? "Unarchived" : "Archived"), onError }),
  });

  return (
    <div className="flex flex-col gap-space-5">
      <PageHeader
        eyebrow="Lead Generator"
        title={g.displayName}
        breadcrumbs={[
          { label: "Lead Generators", to: "/lead-generation/generators" },
          { label: g.displayName },
        ]}
        actions={
          canManage ? (
            <div className="flex items-center gap-space-2">
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
              <OverflowMenu items={actionItems} label="Generator actions" />
            </div>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-4">
        <Card className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
            <Badge status={GENERATOR_STATUS_BADGE[g.status]}>{GENERATOR_STATUS_LABEL[g.status]}</Badge>
            {g.isArchived && <Badge status="archived">Archived</Badge>}
            {g.hasCustomPlan && <Badge status="gold">Custom plan</Badge>}
            <span className="text-caption text-gray-mid">
              {g.portalUserId ? "Portal login linked" : "No portal login yet"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-space-4">
            <DetailField label="Email" value={displayEmail(g.email) ?? g.email} />
            <DetailField label="Phone" value={formatPhone(g.phone) || "—"} />
            <DetailField label="Hired" value={g.hiredAtUtc ? date(g.hiredAtUtc) : "—"} />
            <DetailField label="Added" value={date(g.createdAt)} />
          </div>
          {g.notes && (
            <div className="mt-space-4">
              <div className="text-caption uppercase tracking-wide text-gray-mid">Notes</div>
              <p className="text-small text-onyx whitespace-pre-wrap">{g.notes}</p>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-space-3">
            <h3 className="font-display text-label uppercase tracking-label text-gray-mid m-0">This week's earnings</h3>
            <Link to="/lead-generation/earnings" className="text-label text-gold-shadow hover:underline">
              Review
            </Link>
          </div>
          {summary.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : summary.isError ? (
            <p className="text-small text-status-error">Couldn't load earnings.</p>
          ) : (
            <div className="flex flex-col gap-space-2">
              <BigStat label="Credited (est.)" value={money(summary.data?.totalCredited)} />
              <div className="grid grid-cols-2 gap-space-3">
                <DetailField label="Actions" value={String(summary.data?.totalCount ?? 0)} />
                <DetailField label="Capped" value={money(summary.data?.totalCapped)} />
              </div>
              <p className="text-caption text-gray-mid">Display figure only — paid off-system.</p>
            </div>
          )}
        </Card>
      </div>

      <section>
        <h2 className="font-display text-h3 text-onyx mb-space-3">Recent preleads</h2>
        <DataTable
          columns={columns}
          rows={preleads.data?.data ?? []}
          loading={preleads.isLoading}
          error={preleads.isError ? extractErrorMessage(preleads.error) : null}
          emptyMessage="No preleads sourced yet."
          rowKey={(p) => p.id}
        />
      </section>

      <FeedbackSection generatorId={id} canManage={canManage} />

      {editOpen && <GeneratorFormModal generatorId={id} onClose={() => setEditOpen(false)} />}
    </div>
  );
}

// Manager coaching-feedback thread: leave a note (LeadGen.Manage) + the
// newest-first history with the generator's read/unread status. Reads gate on
// the page's LeadGen.View; the composer is shown only to managers.
function FeedbackSection({ generatorId, canManage }: { generatorId: string; canManage: boolean }) {
  const toast = useToast();
  const { date } = useDateFormatter();
  const feedback = useLeadGenFeedback(generatorId);
  const post = usePostLeadGenFeedback(generatorId);
  const [body, setBody] = useState("");

  const submit = () => {
    const text = body.trim();
    if (!text) return;
    post.mutate(
      { body: text },
      {
        onSuccess: () => {
          setBody("");
          toast.success("Feedback posted");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  const notes = feedback.data ?? [];

  return (
    <section>
      <h2 className="font-display text-h3 text-onyx mb-space-3">Feedback</h2>

      {canManage && (
        <Card className="mb-space-3">
          <label
            htmlFor="lg-feedback-body"
            className="text-caption uppercase tracking-wide text-gray-mid"
          >
            Leave a coaching note
          </label>
          <Textarea
            id="lg-feedback-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Share coaching feedback — it appears in the generator's console."
            className="mt-space-2"
          />
          <div className="mt-space-2 flex justify-end">
            <Button size="sm" onClick={submit} disabled={!body.trim() || post.isPending}>
              {post.isPending ? "Posting…" : "Post feedback"}
            </Button>
          </div>
        </Card>
      )}

      {feedback.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : feedback.isError ? (
        <Card>
          <p className="text-small text-status-error m-0">
            {extractErrorMessage(feedback.error)}
          </p>
        </Card>
      ) : notes.length === 0 ? (
        <EmptyState
          title="No feedback yet"
          description="Coaching notes you leave appear here and in the generator's console."
        />
      ) : (
        <ul className="flex flex-col gap-space-3 list-none p-0 m-0">
          {notes.map((f) => (
            <li key={f.id}>
              <Card className="space-y-space-2">
                <div className="flex flex-wrap items-center justify-between gap-space-2">
                  <span className="text-small font-display font-semibold text-onyx">{f.authorName}</span>
                  <div className="flex items-center gap-space-2">
                    <Badge status={f.isRead ? "finalized" : "gold"}>{f.isRead ? "Read" : "Unread"}</Badge>
                    <span className="text-caption text-gray-mid">{date(f.createdAt)}</span>
                  </div>
                </div>
                <p className="text-small text-onyx whitespace-pre-wrap m-0">{f.body}</p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="text-small text-onyx break-words">{value}</div>
    </div>
  );
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="font-display text-h2 text-onyx tabular-nums">{value}</div>
    </div>
  );
}
